import { useState } from 'react'
import { Outlet, NavLink, useLocation, Link } from 'react-router-dom'
import {
  HomeIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  CogIcon,
  ChartBarIcon as UsageIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { useAppStore, selectRunningScanCount, selectQueuedScanCount } from '../store/appStore'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { shallow } from 'zustand/shallow'
import CyberSecMark from './icons/CyberSecMark'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Terminal', href: '/terminal', icon: ComputerDesktopIcon },
  { name: 'Reports', href: '/reports', icon: DocumentTextIcon },
  { name: 'Targets', href: '/targets', icon: CyberSecMark },
  { name: 'Usage', href: '/usage', icon: UsageIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
]

export default function Layout() {
  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isConnected, currentUser, logout, reportNotifications, dismissReportNotification } = useAppStore(
    (state) => ({
      isConnected: state.isConnected,
      currentUser: state.currentUser,
      logout: state.logout,
      reportNotifications: state.reportNotifications,
      dismissReportNotification: state.dismissReportNotification,
    }),
    shallow
  )
  const runningScanCount = useAppStore(selectRunningScanCount)
  const queuedScanCount = useAppStore(selectQueuedScanCount)
  const location = useLocation()

  const pluralize = (count: number, singular: string, plural?: string) => (
    count === 1 ? `${count} ${singular}` : `${count} ${plural ?? `${singular}s`}`
  )

  const scanStatusLabel = queuedScanCount > 0
    ? `${pluralize(runningScanCount, 'running scan')} · ${pluralize(queuedScanCount, 'queued scan')}`
    : pluralize(runningScanCount, 'running scan')

  const toggleSidebar = () => setSidebarExpanded(!sidebarExpanded)
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen)

  return (
    <div className="min-h-dvh flex overflow-x-hidden bg-gray-900">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-gray-900/70"
            aria-hidden="true"
            onClick={toggleMobileMenu}
          />

          <div className="relative mr-auto flex h-full w-4/5 max-w-sm flex-col bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <CyberSecMark className="h-8 w-8 text-blue-400" />
                <span className="text-xl font-bold text-white">CyberSec</span>
              </div>
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={toggleMobileMenu}
                aria-label="Close menu"
                title="Close menu"
              >
                <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-gray-700">
                {currentUser ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                      {currentUser.email?.charAt(0).toUpperCase()}
                    </div>
                    <span className="block max-w-[11rem] truncate text-sm text-gray-300" title={currentUser.email}>{currentUser.email}</span>
                  </div>
                ) : (
                  <span className="text-gray-400 text-sm">Not signed in</span>
                )}
                <button
                  onClick={() => {
                    logout()
                    toast('Logged out')
                    toggleMobileMenu()
                  }}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-100 text-sm"
                >
                  Logout
                </button>
              </div>

              <nav className="px-3 py-4 space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={toggleMobileMenu}
                      className={clsx(
                        isActive
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                        'group flex items-center gap-3 px-3 py-2 text-base font-medium rounded-md transition-colors'
                      )}
                    >
                      <item.icon
                        className={clsx(
                          isActive ? 'text-gray-300' : 'text-gray-400 group-hover:text-gray-300',
                          'h-6 w-6'
                        )}
                        aria-hidden="true"
                      />
                      <span className="truncate">{item.name}</span>
                    </NavLink>
                  )
                })}
                {currentUser?.role === 'admin' && (
                  <NavLink
                    key="Admin"
                    to="/admin"
                    onClick={toggleMobileMenu}
                    className={clsx(
                      location.pathname === '/admin' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                      'group flex items-center gap-3 px-3 py-2 text-base font-medium rounded-md transition-colors'
                    )}
                  >
                    <CyberSecMark className="h-6 w-6 text-gray-400 group-hover:text-gray-300" />
                    <span className="truncate">Admin</span>
                  </NavLink>
                )}
              </nav>
            </div>

            <div className="border-t border-gray-700 px-4 py-4">
              <div className="flex items-center gap-3">
                <div
                  className={clsx('w-3 h-3 rounded-full', isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500')}
                  aria-hidden="true"
                />
                <div>
                  <p className="text-white text-sm font-medium">{isConnected ? 'Connected' : 'Disconnected'}</p>
                  <p className="text-gray-400 text-xs">{scanStatusLabel}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div
        className={clsx(
          'hidden md:flex md:flex-shrink-0 transition-[width] duration-300 ease-in-out',
          sidebarExpanded ? 'md:w-64' : 'md:w-20'
        )}
        aria-label="Primary navigation"
      >
        <div className="flex flex-col w-full">
          <div className="flex flex-col h-0 flex-1 bg-gray-800">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <CyberSecMark className="h-8 w-8 text-blue-400" />
                {sidebarExpanded && <span className="ml-2 text-xl font-bold text-white truncate">CyberSec</span>}
              </div>

              <nav className="mt-5 flex-1 px-2 space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={clsx(
                        isActive
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors'
                      )}
                      title={!sidebarExpanded ? item.name : undefined}
                    >
                      <item.icon
                        className={clsx(
                          isActive ? 'text-gray-300' : 'text-gray-400 group-hover:text-gray-300',
                          'flex-shrink-0 h-6 w-6',
                          sidebarExpanded ? 'mr-3' : 'mx-auto'
                        )}
                        aria-hidden="true"
                      />
                      {sidebarExpanded && <span className="truncate">{item.name}</span>}
                    </NavLink>
                  )
                })}
                {currentUser?.role === 'admin' && (
                  <NavLink
                    key="Admin"
                    to="/admin"
                    className={clsx(
                      location.pathname === '/admin'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                      'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors'
                    )}
                    title={!sidebarExpanded ? 'Admin' : undefined}
                  >
                    <CyberSecMark
                      className={clsx(
                        location.pathname === '/admin' ? 'text-gray-300' : 'text-gray-400 group-hover:text-gray-300',
                        'flex-shrink-0 h-6 w-6',
                        sidebarExpanded ? 'mr-3' : 'mx-auto'
                      )}
                      aria-hidden="true"
                    />
                    {sidebarExpanded && <span className="truncate">Admin</span>}
                  </NavLink>
                )}
              </nav>

              {/* Sidebar toggle button */}
              <div className="px-2 mt-4">
                <button
                  onClick={toggleSidebar}
                  className="w-full flex items-center justify-center px-2 py-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                  title={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                  {sidebarExpanded ? (
                    <ChevronLeftIcon className="h-5 w-5" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Connection status and user */}
            <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
              {sidebarExpanded ? (
                <div className="flex w-full flex-wrap items-start justify-between gap-3">
                  <div
                    className={clsx(
                      'w-3 h-3 rounded-full mr-3',
                      isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                    )}
                  />
                  <div className="text-sm flex-1">
                    <p className="text-white font-medium">{isConnected ? 'Connected' : 'Disconnected'}</p>
                    <p className="text-gray-400">{scanStatusLabel}</p>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1 min-w-0">
                    {currentUser && (
                      <div className="text-xs text-gray-300 flex items-center justify-end gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">
                          {currentUser.email?.charAt(0).toUpperCase()}
                        </div>
                        <span className="block max-w-[11rem] truncate" title={currentUser.email}>{currentUser.email}</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        logout()
                        toast('Logged out')
                      }}
                      className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs"
                      title="Logout"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                 <div
                   className={clsx(
                     'w-3 h-3 rounded-full mx-auto',
                     isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                   )}
                   title={`${isConnected ? 'Connected' : 'Disconnected'} · ${scanStatusLabel}`}
                 />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden">
          <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
            <button
              type="button"
              className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
              onClick={toggleMobileMenu}
              aria-label="Open menu"
              title="Open menu"
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex-1 px-4 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <CyberSecMark className="h-8 w-8 text-blue-600" />
                <span className="text-xl font-bold text-gray-900 text-balance">CyberSec</span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto overflow-x-hidden focus:outline-none bg-gray-900">
          <div className="h-full">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Report ready prompts */}
      {reportNotifications.length > 0 && (
        <div className="fixed inset-x-4 bottom-4 z-40 flex flex-col items-stretch gap-3 sm:inset-auto sm:right-4 sm:bottom-4 sm:left-auto sm:items-end">
          {reportNotifications.map((notification) => (
            <div
              key={notification.id}
              className="w-full max-w-[calc(100vw-2rem)] sm:w-auto sm:max-w-sm bg-gray-900 border border-blue-500/30 rounded-lg shadow-xl p-4"
            >
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-6 w-6 text-emerald-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">Report ready</p>
                  <p className="text-xs text-gray-300 mt-1 wrap-soft">
                    {notification.target
                      ? `Your scan for ${notification.target} has finished. Review the findings in the report.`
                      : 'Your scan has finished and a report is ready to review.'}
                  </p>
                  <div className="mt-3 toolbar-responsive">
                    <Link
                      to={`/reports/${notification.reportId}`}
                      onClick={() => dismissReportNotification(notification.id)}
                      className="inline-flex w-full items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-500 sm:w-auto"
                    >
                      View report
                    </Link>
                    <button
                      type="button"
                      onClick={() => dismissReportNotification(notification.id)}
                      className="w-full rounded-md bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-300 transition-colors hover:bg-gray-700 sm:w-auto"
                    >
                      Later
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
